import { ZodValidationIssue } from "core/types";
import { z, ZodTypeAny } from "zod";

export function createSimultaneousSchema<T extends ZodTypeAny>(
  baseSchema: T,
  validator: (data: z.infer<T>) => ZodValidationIssue[],
): T {
  return baseSchema.superRefine((data, ctx) => {
    const issues = validator(data);
    issues.forEach((issue) =>
      ctx.addIssue({ ...issue, code: z.ZodIssueCode.custom }),
    );
  });
}

/**
 * Utility functions for handling ActivableTextInputField data transformation
 *
 * The legacy system uses a single field that can be:
 * - null: checkbox unchecked
 * - "": checkbox checked, no description
 * - "text": checkbox checked, with description
 *
 * The React Hook Form uses separate fields:
 * - boolean: checkbox state
 * - string: description text
 */

/**
 * Converts from backend format (null | "" | string) to form format (boolean + description)
 * Used when initializing the form with existing data
 */
export function convertActivableFieldFromBackend(
  value: string | null | undefined | boolean,
): { checked: boolean; description: string | null } {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return { checked: false, description: null };
  }

  // Handle boolean (shouldn't happen from backend, but handle for safety)
  if (typeof value === "boolean") {
    return { checked: value, description: null };
  }

  // Handle string
  if (typeof value === "string") {
    if (value === "") {
      return { checked: true, description: null };
    }
    return { checked: true, description: value };
  }

  // Fallback
  return { checked: false, description: null };
}

/**
 * Converts from form format (boolean + description) to backend format (null | "" | string)
 * Used when submitting the form data
 */
export function convertActivableFieldToBackend(
  checked: boolean | null | undefined,
  description: string | null | undefined,
): string | null {
  if (checked === true) {
    // Checkbox is checked: use description if available, otherwise empty string
    return description ?? "";
  }
  // Checkbox is unchecked: set to null
  return null;
}
